import type { FinancePeriod } from './financeAnalytics';
import type { Invoice } from './types';

/**
 * Export-Protokoll und Nummernkreisprüfung — the GoBD side of the handover.
 *
 * Two things an accountant needs and FORQ could not answer before:
 *   1. WAS THIS ALREADY HANDED OVER? Without a log the same month can be exported twice (booked
 *      twice) or never (booked never), and nobody can tell afterwards which happened.
 *   2. IS THE INVOICE NUMBER RANGE COMPLETE? Gap-free numbering is a GoBD requirement. A missing
 *      RE number means either a document was deleted or one was never handed over — both matter.
 */
export interface ExportLogEntry {
  readonly id: string;
  readonly tenantId: string;
  readonly from: string;
  readonly to: string;
  readonly exportedAt: string;
  readonly exportedBy: string;
  readonly format: ExportFormat;
  readonly rowCount: number;
  /** Sum of the Debitor rows, credit notes deducted — the figure to compare against later. */
  readonly debitorTotal: number;
  readonly kreditorTotal: number;
  /** Fingerprint of the exported content, so a re-export can be recognised as identical or not. */
  readonly checksum: string;
  readonly fileName: string;
}

export type ExportFormat = 'csv' | 'extf';

export const exportFormatLabels: Readonly<Record<ExportFormat, string>> = {
  csv: 'CSV (Prüfliste)',
  extf: 'DATEV EXTF (Import)',
};

export interface RecordExportInput {
  readonly tenantId: string;
  readonly period: FinancePeriod;
  readonly exportedBy: string;
  readonly format: ExportFormat;
  readonly rowCount: number;
  readonly debitorTotal: number;
  readonly kreditorTotal: number;
  readonly checksum: string;
  readonly fileName: string;
}

export interface ExportLogGateway {
  listExports(tenantId: string): Promise<readonly ExportLogEntry[]>;
  recordExport(input: RecordExportInput): Promise<ExportLogEntry>;
}

/**
 * A stable fingerprint of the exported text. FNV-1a: not cryptographic — it only needs to make
 * "same content" and "different content" distinguishable so a repeat export can be classified.
 */
export function checksumOf(content: string): string {
  let hash = 0x811c9dc5;
  for (let index = 0; index < content.length; index += 1) {
    hash ^= content.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(16).padStart(8, '0').toUpperCase();
}

/** Earlier exports whose period overlaps the one about to be exported. */
export function overlappingExports(
  entries: readonly ExportLogEntry[],
  period: FinancePeriod,
): readonly ExportLogEntry[] {
  if (!period.from && !period.to) return entries;
  return entries.filter((entry) => {
    const startsAfter = period.to && entry.from > period.to;
    const endsBefore = period.from && entry.to < period.from;
    return !startsAfter && !endsBefore;
  });
}

export type RepeatKind = 'first' | 'identical' | 'changed';

/**
 * Classifies a pending export against the log. 'identical' is harmless (the same file again);
 * 'changed' means the period was already handed over AND the content has moved since — the case
 * that silently double-books.
 */
export function classifyRepeat(
  entries: readonly ExportLogEntry[],
  period: FinancePeriod,
  checksum: string,
): RepeatKind {
  const previous = overlappingExports(entries, period);
  if (previous.length === 0) return 'first';
  return previous.some((entry) => entry.checksum === checksum) ? 'identical' : 'changed';
}

// ---------------------------------------------------------------------------
// Nummernkreis — GoBD wants the invoice numbers gap-free.
// ---------------------------------------------------------------------------

export interface NumberRangeGap {
  readonly prefix: string;
  readonly from: number;
  readonly to: number;
  readonly count: number;
}

export interface NumberRangeReport {
  readonly prefix: string;
  readonly lowest: number;
  readonly highest: number;
  readonly present: number;
  readonly gaps: readonly NumberRangeGap[];
}

const numberPattern = /^([A-Z]+-\d{2})-(\d+)$/;

/**
 * Splits every legal document number into its range prefix (RE-26, GS-26 …) and sequence, then
 * reports the gaps per range. Cancelled documents COUNT AS PRESENT: a storniertes Dokument keeps
 * its number, and treating it as missing would raise a false alarm on every Storno.
 */
export function checkNumberRanges(invoices: readonly Invoice[]): readonly NumberRangeReport[] {
  const ranges = new Map<string, Set<number>>();
  for (const invoice of invoices) {
    if (!invoice.invoiceNumber) continue;
    const match = numberPattern.exec(invoice.invoiceNumber);
    if (!match) continue;
    const [, prefix, sequence] = match;
    const bucket = ranges.get(prefix) ?? new Set<number>();
    bucket.add(Number(sequence));
    ranges.set(prefix, bucket);
  }

  return [...ranges.entries()]
    .map(([prefix, numbers]) => {
      const sorted = [...numbers].sort((left, right) => left - right);
      const lowest = sorted[0] ?? 0;
      const highest = sorted[sorted.length - 1] ?? 0;
      const gaps: NumberRangeGap[] = [];
      for (let index = 1; index < sorted.length; index += 1) {
        const previous = sorted[index - 1]!;
        const current = sorted[index]!;
        if (current - previous > 1) {
          gaps.push({ prefix, from: previous + 1, to: current - 1, count: current - previous - 1 });
        }
      }
      return { prefix, lowest, highest, present: sorted.length, gaps };
    })
    .sort((left, right) => left.prefix.localeCompare(right.prefix));
}

export function hasNumberGaps(reports: readonly NumberRangeReport[]): boolean {
  return reports.some((report) => report.gaps.length > 0);
}

/** Formats a gap the way a human reads it: "RE-26-000004" or "RE-26-000004 bis RE-26-000006". */
export function formatGap(gap: NumberRangeGap): string {
  const pad = (value: number) => String(value).padStart(6, '0');
  return gap.count === 1
    ? `${gap.prefix}-${pad(gap.from)}`
    : `${gap.prefix}-${pad(gap.from)} bis ${gap.prefix}-${pad(gap.to)}`;
}
